import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut } from '@/lib/api';
import { usePricebookApi } from './usePricebookApi';
import { useApiMutation } from '../useApiMutation';
import { pricebookKeys } from '@/lib/queryKeys';
import { toast } from 'sonner';
import type {
  DynamicPriceBreakdown,
  LaborRateProfile,
  LaborRateProfileKind,
  LaborRateTier,
  MarkupProfile,
  MarkupRange,
} from '@/types/pricebook';

const keys = {
  labor: ['pricebook', 'pricing', 'labor-rates'] as const,
  markup: ['pricebook', 'pricing', 'markup-profiles'] as const,
};

export function useLaborRateProfiles(options: { enabled?: boolean } = {}) {
  const { client } = usePricebookApi();
  return useQuery({
    queryKey: keys.labor,
    queryFn: () => apiGet<LaborRateProfile[]>(client, '/pricebook/pricing/labor-rates'),
    enabled: options.enabled ?? true,
  });
}

export function useMarkupProfiles() {
  const { client } = usePricebookApi();
  return useQuery({
    queryKey: keys.markup,
    queryFn: () => apiGet<MarkupProfile[]>(client, '/pricebook/pricing/markup-profiles'),
  });
}

export function useSaveLaborRateProfile() {
  const { client } = usePricebookApi();
  const queryClient = useQueryClient();
  return useMutation({
    // See the markup profile note: only the fields the API accepts.
    mutationFn: ({
      id,
      name,
      kind,
      tiers,
      marginPercent,
      isActive,
    }: {
      id?: string;
      name: string;
      kind?: LaborRateProfileKind;
      tiers: LaborRateTier[];
      marginPercent?: number | null;
      isActive?: boolean;
    }) => {
      // A margin-share profile prices no time, so it sends no tiers — and an
      // hourly one sends no percentage. Sending both would reach a validator
      // that rejects the pair, and `forbidNonWhitelisted` means a stray field
      // fails the whole save rather than falling away.
      const body =
        kind === 'MARGIN_PERCENT'
          ? { name, kind, marginPercent: marginPercent ?? 0, isActive }
          : { name, kind: 'HOURLY' as const, tiers, isActive };
      return id
        ? apiPut<LaborRateProfile>(client, `/pricebook/pricing/labor-rates/${id}`, body)
        : apiPost<LaborRateProfile>(client, '/pricebook/pricing/labor-rates', body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.labor });
      void queryClient.invalidateQueries({ queryKey: ['pricebook', 'pricing', 'preview'] });
      void queryClient.invalidateQueries({ queryKey: ['pricebook', 'pricing', 'previews'] });
    },
  });
}

export function useSaveMarkupProfile() {
  const { client } = usePricebookApi();
  const queryClient = useQueryClient();
  return useMutation({
    // Only the fields the API accepts. Editing starts from a full profile row,
    // and the API rejects unknown properties outright — sending the whole row
    // back failed every update with "property id should not exist".
    mutationFn: ({
      id,
      name,
      ranges,
      isActive,
    }: {
      id?: string;
      name: string;
      ranges: MarkupRange[];
      isActive?: boolean;
    }) => {
      // `ranges` carries the profile's basis and scope; both are declared on
      // MarkupRangeDto, so they survive `forbidNonWhitelisted`.
      const body = { name, ranges, isActive };
      return id
        ? apiPut<MarkupProfile>(client, `/pricebook/pricing/markup-profiles/${id}`, body)
        : apiPost<MarkupProfile>(client, '/pricebook/pricing/markup-profiles', body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.markup });
      void queryClient.invalidateQueries({ queryKey: ['pricebook', 'pricing', 'preview'] });
      void queryClient.invalidateQueries({ queryKey: ['pricebook', 'pricing', 'previews'] });
    },
  });
}

export function useDynamicPricePreview(serviceId?: string, enabled = true) {
  const { client } = usePricebookApi();
  return useQuery({
    queryKey: ['pricebook', 'pricing', 'preview', serviceId],
    enabled: Boolean(serviceId) && enabled,
    queryFn: () =>
      apiPost<DynamicPriceBreakdown>(client, `/pricebook/pricing/services/${serviceId}/preview`, {
        afterHours: false,
      }),
  });
}

export function useDynamicPricePreviews(serviceIds: string[]) {
  const { client } = usePricebookApi();
  const ids = [...new Set(serviceIds)].sort();
  return useQuery({
    queryKey: ['pricebook', 'pricing', 'previews', ids],
    enabled: ids.length > 0,
    queryFn: () =>
      apiPost<Record<string, DynamicPriceBreakdown>>(
        client,
        '/pricebook/pricing/services/preview-many',
        { ids },
      ),
  });
}

/**
 * @param options.notify announce the new price. On by default — applying is
 * normally the operator's whole action. A save that ends by recalculating has
 * already said "Item updated", and two toasts for one click read as two things
 * having happened.
 */
export function useApplyDynamicPrice(options: { notify?: boolean } = {}) {
  const { client } = usePricebookApi();
  const queryClient = useQueryClient();
  const notify = options.notify ?? true;

  return useApiMutation<DynamicPriceBreakdown, string | { serviceId: string; initial?: boolean }>({
    mutationFn: (input) => {
      const { serviceId, initial } =
        typeof input === 'string' ? { serviceId: input, initial: false } : input;
      // `initial` is the create page pricing a service it has just written —
      // the first figure is not a price change and publishes no version.
      return apiPost<DynamicPriceBreakdown>(
        client,
        `/pricebook/pricing/services/${serviceId}/apply-price${initial ? '?initial=true' : ''}`,
        {},
      );
    },
    onSuccess: (_result, input) => {
      const serviceId = typeof input === 'string' ? input : input.serviceId;
      void queryClient.invalidateQueries({ queryKey: pricebookKeys.detail(serviceId) });
      void queryClient.invalidateQueries({ queryKey: pricebookKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: ['pricebook', 'pricing', 'preview'] });
      void queryClient.invalidateQueries({ queryKey: ['pricebook', 'pricing', 'previews'] });
      // "Pricing", not "price": this applies the derived COST too, and a
      // service whose parts got cheaper without moving its price would be told
      // its price changed when it did not.
      if (notify) toast.success('Service pricing updated from its latest inputs');
    },
  });
}
